import {
  Lock, Shield, CheckCircle2, ArrowRight,
  Layers, Bath, DoorOpen, ChefHat, Paintbrush, Home, Leaf,
  ShieldAlert,
} from "lucide-react";

const STRATEGY_ICONS = {
  category_shift_bedroom: DoorOpen,
  internal_reconfiguration: Layers,
  kitchen_reconfiguration: ChefHat,
  bathroom_additional: Bath,
  bathroom_ensuite: Bath,
  secondary_space_separation: DoorOpen,
  cosmetic_perception: Paintbrush,
  curb_appeal: Home,
  energy_future_proofing: Leaf,
};

export function WhatToDoNext({ valueHeadroom, isUnlocked, confidence }) {
  const strategies = valueHeadroom?.strategies || [];
  const supportive = valueHeadroom?.supportive_strategies || [];
  const valueTraps = valueHeadroom?.value_traps || [];
  const allStrategies = [...strategies, ...supportive];
  const top = strategies[0];

  return (
    <div data-testid="section-what-to-do" className="space-y-3">
      {/* Start Here */}
      {top && (
        <div
          className="bg-cipher-green/10 border border-cipher-green/20 rounded-xl p-3.5"
          data-testid="start-here-card"
        >
          <div className="flex items-center gap-2 mb-1.5">
            <ArrowRight className="w-3.5 h-3.5 text-cipher-green" />
            <span className="text-[10px] font-bold text-cipher-green uppercase tracking-wider">
              Start Here
            </span>
          </div>
          <p className="text-sm font-semibold text-white">{top.label}</p>
          <p className="text-xs text-white/50 mt-1 leading-relaxed line-clamp-2">
            {top.description}
          </p>
        </div>
      )}

      {/* 3-Step Path */}
      <div className="flex gap-0">
        {["Plan & Quote", top?.label || "Top improvement", "Finish & Present"].map(
          (label, i) => (
            <div key={i} className="flex-1 flex flex-col items-center text-center px-1">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold mb-1 ${
                  i === 0
                    ? "bg-cipher-green/20 text-cipher-green border border-cipher-green/30"
                    : "bg-white/10 text-white/40 border border-white/10"
                }`}
              >
                {i + 1}
              </div>
              <p className="text-[10px] text-white/50 leading-tight">{label}</p>
              {i < 2 && (
                <div className="absolute" style={{ display: "none" }} />
              )}
            </div>
          )
        )}
      </div>

      {/* Priority Ranking */}
      {allStrategies.length > 1 && (
        <div className="relative bg-white/[0.04] rounded-xl overflow-hidden">
          {/* #1 visible */}
          <div className="flex items-center gap-2.5 px-3 py-2.5 border-b border-white/5">
            <span className="w-5 h-5 rounded-full bg-cipher-green/20 text-cipher-green text-[10px] font-bold flex items-center justify-center">
              1
            </span>
            <p className="text-sm text-white/80 flex-1">{allStrategies[0].label}</p>
            <CheckCircle2 className="w-3.5 h-3.5 text-cipher-green/50" />
          </div>

          {/* Rest — blurred when locked */}
          <div className="relative">
            {!isUnlocked && (
              <div className="absolute inset-0 z-10 backdrop-blur-[3px] bg-black/20 flex items-center justify-center">
                <div className="flex items-center gap-1.5 bg-black/50 px-3 py-1.5 rounded-full border border-white/10">
                  <Lock className="w-3 h-3 text-white/40" />
                  <span className="text-[11px] text-white/50">
                    {allStrategies.length - 1} more ranked
                  </span>
                </div>
              </div>
            )}
            <div className={!isUnlocked ? "select-none" : ""}>
              {allStrategies.slice(1, 4).map((s, i) => (
                <div
                  key={s.id}
                  className="flex items-center gap-2.5 px-3 py-2 border-b border-white/5 last:border-b-0"
                >
                  <span className="w-5 h-5 rounded-full bg-white/10 text-white/30 text-[10px] font-bold flex items-center justify-center">
                    {i + 2}
                  </span>
                  <p className="text-xs text-white/50 flex-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Mistakes to avoid — compact */}
      {valueTraps.length > 0 && !isUnlocked && (
        <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/[0.06] border border-amber-500/15 rounded-lg">
          <ShieldAlert className="w-3.5 h-3.5 text-amber-400/50 flex-shrink-0" />
          <span className="text-xs text-amber-300/60">
            {valueTraps.length} mistake{valueTraps.length > 1 ? "s" : ""} to
            avoid —{" "}
            <span className="text-amber-400/80 font-medium">unlock to see</span>
          </span>
        </div>
      )}
      {valueTraps.length > 0 && isUnlocked && (
        <div className="space-y-1.5">
          {valueTraps.map((t) => (
            <div
              key={t.id}
              className="flex items-start gap-2 px-3 py-2 bg-amber-500/[0.06] border border-amber-500/15 rounded-lg"
            >
              <ShieldAlert className="w-3 h-3 text-amber-400/60 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-300/70">{t.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Confidence — one line */}
      <div className="flex items-center gap-2 pt-1">
        <Shield
          className={`w-3.5 h-3.5 ${
            confidence?.level === "High"
              ? "text-cipher-green"
              : confidence?.level === "Low"
              ? "text-amber-400"
              : "text-blue-400"
          }`}
        />
        <span className="text-xs text-white/40">
          {confidence?.level} confidence · {confidence?.reason}
        </span>
      </div>
    </div>
  );
}
